import { useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '../../../buttons/button/Button.js';
import { useId } from '../../../core/hooks/useId.js';
import { getOrCreateNamedHighlight } from '../components/CSSHighlight.js';
import { FilterStatement } from '../components/FilterStatement.js';
import { useFilterFieldContext } from '../hooks/useFilterFieldContext.js';
import { useFilterFieldLoggerContext } from '../hooks/useFilterFieldLoggerContext.js';
import { useShowMore } from '../hooks/useShowMore.js';
import { filterFieldMessages } from '../messages.js';
import * as styles from './RecentPinnedSuggestionItem.sty.js';
import * as suggestionStyles from './Suggestion.sty.js';
import { Suggestion } from './Suggestion.js';
import type { FilterFieldSuggestion } from './Suggestion.types.js';
import { SuggestionGroup } from './SuggestionGroup.js';
import { SuggestionGroupLabel } from './SuggestionGroupLabel.js';
import { SuggestionPin } from './SuggestionPin.js';

/**
 * @internal
 */
function RecentAndPinnedSuggestionItem(
  props: Readonly<{
    suggestion: FilterFieldSuggestion;
    index: number;
    ranges: Array<[number, number]>;
  }>,
) {
  const { suggestion, index, ranges, ...remainingProps } = props;

  const suggestionRef = useRef<HTMLDivElement>(null);

  const id = useId();

  const { logger } = useFilterFieldLoggerContext();

  const suggestionHighlight = useMemo(
    () => getOrCreateNamedHighlight('filterfield-suggestions-highlight'),
    [],
  );

  useEffect(() => {
    const highlightRanges: Array<Range> = [];
    if (suggestionRef.current) {
      const valueElement = suggestionRef.current.querySelector(
        `.${suggestionStyles.value} span`,
      );
      if (valueElement && valueElement.textContent !== '') {
        const textNode = valueElement.childNodes.item(0);
        if (textNode.nodeType === Node.TEXT_NODE) {
          const textLength = textNode.textContent?.length ?? 0;

          for (const [start, end] of ranges) {
            // Clamp the start and end to the length of the text content to avoid errors when creating ranges.
            const clampedStart = Math.min(start, textLength);
            const clampedEnd = Math.min(end, textLength);

            if (clampedStart >= clampedEnd) {
              continue;
            }

            try {
              const range = new Range();
              range.setStart(textNode, clampedStart);
              range.setEnd(textNode, clampedEnd);
              suggestionHighlight.add(range);
              highlightRanges.push(range);
            } catch (error) {
              logger?.warn(
                'FilterField suggestion highlight range error',
                error,
              );
            }
          }
        }
      }
    }
    return () => {
      if (highlightRanges) {
        for (const range of highlightRanges) {
          suggestionHighlight.delete(range);
        }
      }
    };
  }, [logger, ranges, suggestionHighlight]);

  return (
    <Suggestion
      className={styles.recentSuggestion}
      key={`${suggestion.value ?? 'non-interactive-'}${index}`}
      ref={suggestionRef}
      {...suggestion}
      {...remainingProps}
    >
      <FilterStatement
        value={suggestion.displayValue?.toString()}
        highlighting="syntax"
      />
      {suggestion.value ? (
        <SuggestionPin
          // Role needs to be option, because otherwise NVDA will crash if the activeDecendant of a listbox is anything other than a role option or group.
          role="option"
          filterString={suggestion.value}
          className={styles.suggestionPin}
          placement="right"
          fallbackPlacements={['right', 'left']}
          id={id}
          data-strato-filter-field-virtual-focus-id={id}
          data-strato-filter-field-virtual-focus-type="pinned-suggestion"
        />
      ) : null}
    </Suggestion>
  );
}

/**
 * @internal
 */
export function RecentSuggestionGroup() {
  const { filterFieldId, recentSuggestionValues } = useFilterFieldContext();

  let primaryCount = 2;
  if (recentSuggestionValues.length === 3) {
    primaryCount = 3;
  }

  const {
    items: slicedRecentSuggestionValues,
    isSliced,
    controlProps,
  } = useShowMore({
    items: recentSuggestionValues,
    primaryCount,
  });

  if (recentSuggestionValues.length === 0) {
    return null;
  }

  return (
    <>
      <SuggestionGroup
        aria-labelledby={`${filterFieldId ?? ''}-recent-filters`}
      >
        <SuggestionGroupLabel id={`${filterFieldId ?? ''}-recent-filters`}>
          <FormattedMessage {...filterFieldMessages.suggestions.recentlyUsed} />
        </SuggestionGroupLabel>
        {slicedRecentSuggestionValues?.map((recentItem, index) => (
          <RecentAndPinnedSuggestionItem
            suggestion={recentItem.suggestion}
            index={index}
            key={recentItem.suggestion.value ?? index}
            data-strato-filter-field-virtual-focus-type="recent-suggestion"
            ranges={recentItem.ranges}
          />
        ))}
      </SuggestionGroup>
      {isSliced && (
        <div role="presentation">
          <Button {...controlProps} />
        </div>
      )}
    </>
  );
}

/**
 * @internal
 */
export function PinnedSuggestionGroup() {
  const { filterFieldId, pinnedSuggestionValues } = useFilterFieldContext();

  if (pinnedSuggestionValues.length === 0) {
    return null;
  }

  return (
    <SuggestionGroup aria-labelledby={`${filterFieldId ?? ''}-pinned-filters`}>
      <SuggestionGroupLabel id={`${filterFieldId ?? ''}-pinned-filters`}>
        <FormattedMessage {...filterFieldMessages.suggestions.pinnedFilters} />
      </SuggestionGroupLabel>
      {pinnedSuggestionValues?.map((pinnedItem, index) => (
        <RecentAndPinnedSuggestionItem
          suggestion={pinnedItem.suggestion}
          index={index}
          key={pinnedItem.suggestion.value ?? index}
          data-strato-filter-field-virtual-focus-type="pinned-suggestion"
          ranges={pinnedItem.ranges}
        />
      ))}
    </SuggestionGroup>
  );
}
